/* eslint-disable @typescript-eslint/no-empty-function */
import { ActionsDropdown } from '@molecules';
import { PageListProps, PageListTemplate } from '@templates';
import { TableColumnType, Tag } from 'antd';
import { format, parseISO } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CouponDigital } from 'src/models';

import {
  getInfoStatus,
  parseOrderFilter,
  tableColumnCouponDigitalOrderDefined,
} from './utils/table.couponDigital';

export interface TableCouponDigitalProps {
  filterComponent: React.ReactNode;
  data?: CouponDigital.TCouponResponse;
  onChangePagination?: (take: number, skip: number, pagination: number) => void;
  onEditCoupon?: (value: CouponDigital.IDigitalCoupon) => void;
  onDeleteCoupon?: (value: CouponDigital.IDigitalCoupon) => void;
  onPreviewCoupon?: (value: CouponDigital.IDigitalCoupon) => void;
  onChangeOrder?: (order: string) => void;
  isLoading: boolean;
}

export type TColumnCouponDigitalOrder = {
  column: string;
  inverse: boolean;
};

export const TableDigitalCoupon: React.FC<TableCouponDigitalProps> = ({
  filterComponent,
  data,
  onChangePagination,
  isLoading,
  onChangeOrder,
}) => {
  const [orderColumn, setOrderColumn] =
    useState<TColumnCouponDigitalOrder | null>(null);
  const navigate = useNavigate();

  const handleChangeOrder = useCallback(
    ({ key }: Pick<TableColumnType<CouponDigital.IDigitalCoupon>, 'key'>) => {
      if (!key) return;

      const column = parseOrderFilter(String(key));

      if (!orderColumn || orderColumn.column !== column) {
        setOrderColumn({ column, inverse: false });
        return;
      }

      if (orderColumn.inverse) {
        setOrderColumn(null);
        return;
      }

      setOrderColumn({ column, inverse: true });
    },
    [orderColumn]
  );

  useEffect(() => {
    if (!onChangeOrder || isLoading || !data) return;
    onChangeOrder(
      !orderColumn
        ? ''
        : `[${orderColumn.column},${orderColumn.inverse ? 'DESC' : 'ASC'}]`
    );
  }, [orderColumn]);

  const handleViewCoupon = (coupon: CouponDigital.IDigitalCoupon) => {
    navigate('/screens/digital/coupons/read', { state: { coupon } });
  };

  const headerColumns: PageListProps<CouponDigital.IDigitalCoupon>['columns'] =
    [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: '100px',
        render: (coupon: CouponDigital.IDigitalCoupon['id']) => {
          return coupon;
        },
      },
      {
        title: 'Código do Cupom',
        dataIndex: 'code',
        key: 'code',
        width: '200px',
        render: (code: CouponDigital.IDigitalCoupon['code']) => {
          return code;
        },
      },
      {
        title: 'Descrição',
        dataIndex: 'description',
        key: 'description',
        width: '260px',
        render: (description: CouponDigital.IDigitalCoupon['description']) => {
          return description;
        },
      },
      {
        title: 'Tipo de Desconto',
        dataIndex: 'discount',
        key: 'discount',
        width: '150px',
        render: (discount: CouponDigital.IDigitalCoupon['discount']) => {
          return <div>{CouponDigital.getLabelType(discount.type)}</div>;
        },
      },
      {
        title: 'Valor',
        dataIndex: 'discount',
        key: 'discount',
        width: '150px',
        render: (discount: CouponDigital.IDigitalCoupon['discount']) => {
          return discount.type === CouponDigital.TypeEnum.FIXED
            ? new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(discount.value)
            : `${discount.value}%`;
        },
      },
      {
        title: 'Data de início',
        dataIndex: 'startDate',
        key: 'startDate',
        width: '156px',
        sorter: true,
        sortOrder: tableColumnCouponDigitalOrderDefined(
          'startDate',
          orderColumn
        ),
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
        render: (startDate: CouponDigital.IDigitalCoupon['startDate']) => {
          // return startDate;
          return <div>{format(new Date(startDate), 'dd/MM/yyyy')}</div>;
        },
      },
      {
        title: 'Data Final',
        dataIndex: 'endDate',
        key: 'endDate',
        width: '156px',
        sorter: true,
        sortOrder: tableColumnCouponDigitalOrderDefined('endDate', orderColumn),
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
        render: (endDate: CouponDigital.IDigitalCoupon['endDate']) => {
          // return endDate;
          return <div>{format(parseISO(endDate), 'dd/MM/yyyy')}</div>;
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: '150px',
        render: (status: CouponDigital.IDigitalCoupon['status']) => {
          const { Icon, color, text } = getInfoStatus(status);

          return (
            <Tag icon={<Icon />} color={color}>
              {text}
            </Tag>
          );
        },
      },
      {
        title: 'Ações',
        key: 'action',
        dataIndex: 'index',
        width: '2%',
        render: (_text, record) => (
          <ActionsDropdown
            hideDelete
            onClickEditOption={() => {
              navigate(`/screens/digital/coupons/${record.id}`);
            }}
            onClickPreviewOption={() => {
              handleViewCoupon(record);
            }}
          />
        ),
      },
    ];

  if (!data && isLoading) return null;

  return (
    <PageListTemplate
      filterComponent={filterComponent}
      columns={headerColumns}
      data={data}
      loading={isLoading}
      onChange={onChangePagination}
    />
  );
};
